import { ApolloProvider } from '@apollo/client';
import { useErrorHandler } from '@sixfold/app-utils';
import { RouterProvider, SpinnerOverlay, useNotifications } from '@sixfold/common-ui';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Navigate,
  Route,
  Routes as RouterRoutes,
  BrowserRouter as Router,
  useLocation,
  useNavigate,
} from 'react-router';

import { ChatProvider } from './components/chatbot';
import { createApolloClient } from './lib/apollo_client';
import { getRedirectSearchParam, useIsAuthenticated } from './lib/authorization';
import { getEmbedConfig } from './lib/data';
import { AuthService } from './lib/login';
import { Routes } from './routes';
import { LoginContainer } from './sessions/containers/login_container';
import { WorkspaceContainer } from './workspace/containers/workspace_container';

import '@sixfold/design-tokens/build/transporeon/css/fonts.css';
import '@sixfold/design-tokens/build/trimble/css/fonts.css';
import './local_variables.css';

interface Props {
  authService: AuthService;
}

function LoginRoute({ authService }: { authService: AuthService }) {
  const config = getEmbedConfig();
  const googleClientId = config?.google_client_id;

  if (googleClientId === undefined) {
    throw new Error('Google client id is not specified');
  }

  return (
    <>
      <Helmet title="Login" />
      <LoginContainer authService={authService} google_client_id={googleClientId} />
    </>
  );
}

const HomeTitle: React.FC = () => {
  const location = useLocation();
  if (location.pathname === '/') {
    return <Helmet title="Home" />;
  }
  return null;
};

const RouterProviderBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const { pathname: locationPathname } = useLocation();

  const getCurrentPropValue = useCallback(
    (href: string) => {
      const hrefPathname = new URL(href, window.location.origin).pathname;
      if (locationPathname === hrefPathname) {
        return 'page' as const;
      }
      if (hrefPathname === '/') {
        return undefined;
      }
      const prefix = hrefPathname.endsWith('/') ? hrefPathname : `${hrefPathname}/`;
      if (locationPathname.startsWith(prefix)) {
        return true;
      }
      return undefined;
    },
    [locationPathname],
  );

  return (
    <RouterProvider navigate={navigate} getCurrentPropValue={getCurrentPropValue}>
      {children}
    </RouterProvider>
  );
};

const AppRoute: React.FunctionComponent = () => {
  const { error } = useNotifications();
  const handleError = useErrorHandler();

  const apolloClient = useMemo(() => {
    return createApolloClient({
      handleError,
      onAuthenticationError: (action) => {
        const isLoginWithRedirect =
          window.location.pathname.startsWith(Routes.Login.routerPath) &&
          new URLSearchParams(window.location.search).has('redirect');
        if (isLoginWithRedirect) {
          return;
        }

        if (action === 'logout') {
          window.location.assign(Routes.Logout.generatePath({}));
          return;
        }

        window.location.assign(`${Routes.Login.generatePath({})}${getRedirectSearchParam()}`);
      },
      onGraphQLError: (message) => {
        error({ title: 'Error', message });
      },
    });
  }, [handleError, error]);

  const logout = useCallback(async () => {
    window.location.assign(Routes.Logout.generatePath({}));
  }, []);

  const { isAuthenticated, isLoading, shouldClearSession } = useIsAuthenticated(apolloClient);

  useEffect(() => {
    if (shouldClearSession) {
      window.location.assign(Routes.Logout.generatePath({}));
    }
  }, [shouldClearSession]);

  if (isLoading || shouldClearSession) {
    return <SpinnerOverlay className="loading" />;
  }

  if (!isAuthenticated) {
    return <Navigate to={{ pathname: Routes.Login.generatePath({}), search: getRedirectSearchParam() }} replace />;
  }

  return (
    <ApolloProvider client={apolloClient}>
      <RouterProviderBridge>
        <HomeTitle />
        <ChatProvider>
          <WorkspaceContainer onSignout={logout} />
        </ChatProvider>
      </RouterProviderBridge>
    </ApolloProvider>
  );
};

const App: React.FunctionComponent<Props> = ({ authService }) => {
  return (
    <>
      <Helmet defaultTitle="Sixfold" titleTemplate="%s - Sixfold" />
      <Router>
        <RouterRoutes>
          <Route path={Routes.Login.routerPath} element={<LoginRoute authService={authService} />} />
          <Route path={Routes.App.routerPath} element={<AppRoute />} />
        </RouterRoutes>
      </Router>
    </>
  );
};

export { App };
